import * as React from "react";
import { useMemo } from "react";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";

const getTypeClass = (type) => {
  switch (type) {
    case "Bug":
      return "release-type release-type--bug";
    case "New Feature":
      return "release-type release-type--feature";
    case "Technical Debt":
      return "release-type release-type--technical";
    default:
      return "release-type";
  }
};

export default function CustomizedTimeline(props) {
  const sortedReleaseNotes = useMemo(() => {
    return [...props.releaseNotes].sort((a, b) => {
      const dateA = new Date(`${a.attributes.date}T00:00:00`).getTime();
      const dateB = new Date(`${b.attributes.date}T00:00:00`).getTime();
      return dateB - dateA;
    });
  }, [props.releaseNotes]);

  let prevDate = "";

  return (
    <section className="dashboard-card updates-card">
      <div className="card-header">
        <div>
          <p className="card-eyebrow">Recent Updates</p>
          <h3>What&apos;s New</h3>
        </div>
      </div>

      <Timeline
        className="release-timeline"
        sx={{
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.18,
          },
        }}
      >
        {sortedReleaseNotes.map(({ id, attributes }) => {
          const isNewDate = attributes.date !== prevDate;
          prevDate = attributes.date;

          return (
            <TimelineItem className="release-timeline__item" key={id}>
              <TimelineOppositeContent
                className="release-timeline__date"
                sx={{ m: "auto 0" }}
                align="right"
                variant="body2"
              >
                {isNewDate && attributes.date}
              </TimelineOppositeContent>
              <TimelineSeparator className="release-timeline__separator">
                <TimelineConnector />
                <span className="update-icon update-icon--new release-timeline__dot" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="release-timeline__content">
                <article className="update-entry-expandable release-update-card">
                  <div className="update-title-template">
                    <div className="update-title-template__main">
                      <div className="update-copy">
                        <div className="release-detail-title-row">
                          <Typography
                            variant="h2"
                            component="h2"
                            className="release-card__title"
                          >
                            {attributes.title}
                          </Typography>
                          {attributes.jira_ticket_number && (
                            <span className="text-muted">
                              {attributes.jira_ticket_number}
                            </span>
                          )}
                        </div>
                        <div className="update-time">Release update</div>
                      </div>
                    </div>
                    <span className={getTypeClass(attributes.type)}>
                      {attributes.type}
                    </span>
                  </div>

                  <div className="update-details release-update-card__details">
                    <div className="update-detail">
                      <Typography className="text-muted change-description">
                        {attributes.description}
                      </Typography>
                      <div className="service-chips">
                        {attributes.services_affected.map((service) => (
                          <Chip
                            className="service-chip"
                            label={service}
                            color="primary"
                            variant="outlined"
                            key={service}
                          />
                        ))}
                      </div>
                    </div>
                    {attributes.breaking_change &&
                      attributes.services_affected.includes("Public API") && (
                        <Alert
                          className="alert-box"
                          variant="outlined"
                          severity="warning"
                        >
                          <AlertTitle>Warning</AlertTitle>This change is made on
                          a new version of the API endpoint, the current version
                          will be deprecated 1 year from the release date above,
                          for more information on the request/response models
                          please refer to the{" "}
                          <a href="https://publicapi.k3imagine.com/swagger/index.html">
                            swagger documentation
                          </a>
                        </Alert>
                      )}
                    {attributes.connector_reboot_required && (
                      <Alert
                        className="alert-box"
                        variant="outlined"
                        severity="warning"
                      >
                        <AlertTitle>Warning</AlertTitle>This change requires a
                        connector reboot for the changes to take effect.
                      </Alert>
                    )}
                  </div>
                </article>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </section>
  );
}
